import React, { useCallback, useRef, useState } from 'react';
import { Divider, IconButton, Typography } from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { SBSection, SourceBook } from '@dndModels';
import API from '@utilities/smart_api';
import SBSectionContentViewer from './SBSectionContentViewer';

const api = API.getInstance();

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function flatten(sections: SBSection[], depth = 0): { section: SBSection; depth: number }[] {
  const result: { section: SBSection; depth: number }[] = [];
  for (const s of sections) {
    result.push({ section: s, depth });
    result.push(...flatten(s.subsections, depth + 1));
  }
  return result;
}

function getAncestorAndSelfIds(
  nodes: { section: SBSection; depth: number }[],
  targetId: string
): Set<string> {
  const idx = nodes.findIndex(n => n.section.true_id === targetId);
  if (idx === -1) return new Set();
  const ids = new Set<string>([targetId]);
  let depth = nodes[idx].depth;
  for (let i = idx - 1; i >= 0 && depth > 0; i--) {
    if (nodes[i].depth < depth) {
      ids.add(nodes[i].section.true_id);
      depth = nodes[i].depth;
    }
  }
  return ids;
}

/**
 * Walk up the flat node list from `idx` to find the nearest ancestor (or self)
 * that is a single_page root — i.e. the section that should actually be
 * displayed when any of its single_page descendants are selected.
 */
function getSinglePageRoot(
  nodes: { section: SBSection; depth: number }[],
  idx: number
): { section: SBSection; idx: number } {
  // Walk up: if any ancestor has single_page=true, that ancestor is the root to show.
  // We keep walking until we find the topmost single_page ancestor.
  let rootIdx = idx;
  let depth = nodes[idx].depth;
  for (let i = idx - 1; i >= 0 && depth > 0; i--) {
    if (nodes[i].depth < depth) {
      if (nodes[i].section.single_page) {
        rootIdx = i;
      }
      depth = nodes[i].depth;
    }
  }
  return { section: nodes[rootIdx].section, idx: rootIdx };
}

function getChildIds(
  nodes: { section: SBSection; depth: number }[],
  idx: number
): string[] {
  const parentDepth = nodes[idx].depth;
  const ids: string[] = [];
  for (let i = idx + 1; i < nodes.length; i++) {
    if (nodes[i].depth <= parentDepth) break;
    if (nodes[i].depth === parentDepth + 1) ids.push(nodes[i].section.true_id);
  }
  return ids;
}

// ---------------------------------------------------------------------------
// TreeNode
// ---------------------------------------------------------------------------

interface TreeNodeProps {
  nodes: { section: SBSection; depth: number }[];
  idx: number;
  expandedIds: Set<string>;
  selectedId: string | null;
  onToggleExpand: (id: string) => void;
  onSelect: (id: string, idx: number) => void;
}

const TreeNode = React.memo(function TreeNode({
  nodes, idx, expandedIds, selectedId, onToggleExpand, onSelect,
}: TreeNodeProps) {
  const { section, depth } = nodes[idx];
  const tid = section.true_id;
  const expanded = expandedIds.has(tid);
  const selected = selectedId === tid;

  // Hide if any ancestor is collapsed
  if (depth > 0) {
    let j = idx - 1;
    while (j >= 0 && nodes[j].depth >= depth) j--;
    if (j >= 0 && !expandedIds.has(nodes[j].section.true_id)) return null;
    let ancestor = j;
    while (ancestor > 0) {
      let k = ancestor - 1;
      while (k >= 0 && nodes[k].depth >= nodes[ancestor].depth) k--;
      if (k < 0) break;
      if (!expandedIds.has(nodes[k].section.true_id)) return null;
      ancestor = k;
    }
  }

  const childCount = getChildIds(nodes, idx).length;

  return (
    <div style={{ marginLeft: depth * 16 }}>
      <div
        onClick={() => onSelect(tid, idx)}
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '3px 6px',
          borderRadius: 4,
          cursor: 'pointer',
          backgroundColor: selected ? 'rgba(25, 118, 210, 0.12)' : 'transparent',
          fontWeight: selected ? 600 : 400,
          userSelect: 'none',
        }}
      >
        <span style={{ flex: 1, fontSize: '0.875rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {section.name || <em style={{ color: '#888' }}>Untitled Section</em>}
        </span>
        {childCount > 0 && (
          <IconButton
            size="small"
            onClick={e => { e.stopPropagation(); onToggleExpand(tid); }}
            style={{ padding: 2 }}
          >
            {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        )}
      </div>
    </div>
  );
});

// ---------------------------------------------------------------------------
// SBSectionsViewer
// ---------------------------------------------------------------------------

interface SBSectionsViewerProps {
  sections: SBSection[];
  source_book: SourceBook;
  topHeight: number;
}

export default function SBSectionsViewer({ sections, source_book, topHeight }: SBSectionsViewerProps) {
  const [nodes] = useState(() => flatten(sections));
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  // The section actually being rendered (may be a single_page root, not the clicked node)
  const [displaySection, setDisplaySection] = useState<SBSection | null>(null);
  // The true_id the user clicked — used to scroll-to after load.
  // Stored in a ref so changing it never causes a re-render or invalidates callbacks.
  const scrollTargetIdRef = useRef<string | null>(null);
  // The tree highlight follows the clicked node
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const contentPanelRef = useRef<HTMLDivElement | null>(null);

  const toggleExpand = useCallback((id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  const handleSelect = useCallback((tid: string, idx: number) => {
    setSelectedId(tid);

    // Open ancestors in the tree
    setExpandedIds(prev => {
      const next = new Set(prev);
      getAncestorAndSelfIds(nodes, tid).forEach(id => next.add(id));
      return next;
    });

    // If this node is inside a single_page ancestor, display that root instead
    const { section: rootSection, idx: rootIdx } = getSinglePageRoot(nodes, idx);
    const isSinglePageChild = rootIdx !== idx;

    const alreadyDisplayed = displaySection?.true_id === rootSection.true_id;
    const scrollToId = isSinglePageChild ? tid : rootSection.true_id;

    if (alreadyDisplayed) {
      // Content is already rendered — scroll immediately
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          contentPanelRef.current?.querySelector<HTMLElement>(
            `#sb-section-${scrollToId}`
          )?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      });
      scrollTargetIdRef.current = null;
    } else {
      // Content needs to load first — handleAllLoaded will scroll after
      scrollTargetIdRef.current = isSinglePageChild ? tid : null;
    }

    setDisplaySection(rootSection);
  }, [nodes, displaySection]);

  /**
   * Called by SBSectionContentViewer once itself and all its children are loaded.
   * Stable reference via useCallback with no deps — reads scroll target from a ref.
   */
  const handleAllLoaded = useCallback((_rootSectionId: string) => {
    const scrollTargetId = scrollTargetIdRef.current;
    if (!scrollTargetId) return;
    scrollTargetIdRef.current = null;
    // Defer until after React has committed and painted the new children
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const targetEl = contentPanelRef.current?.querySelector<HTMLElement>(
          `#sb-section-${scrollTargetId}`
        );
        targetEl?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  }, []);

  if (nodes.length === 0) {
    return <span style={{ color: '#888' }}>No sections.</span>;
  }

  return (
    <div style={{
      display: 'flex',
      alignItems: 'flex-start',
      border: '1px solid rgba(0,0,0,0.12)',
      borderRadius: 4,
      width: '100%',
    }}>
      {/* Tree panel */}
      <div style={{
        width: '25%',
        flexShrink: 0,
        borderRight: '1px solid rgba(0,0,0,0.12)',
        position: 'sticky',
        top: 0,
        height: `calc(100vh - ${topHeight + 64 + 2}px)`,
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'auto',
      }}>
        <div style={{ padding: '8px 6px 4px', flexShrink: 0 }}>
          <Typography variant="subtitle2" style={{ color: '#555' }}>
            {source_book.name}
          </Typography>
          <Divider style={{ marginTop: 4 }} />
        </div>
        <div style={{ padding: '4px' }}>
          {nodes.map((node, i) => (
            <TreeNode
              key={node.section.true_id}
              nodes={nodes}
              idx={i}
              expandedIds={expandedIds}
              selectedId={selectedId}
              onToggleExpand={toggleExpand}
              onSelect={handleSelect}
            />
          ))}
        </div>
      </div>

      {/* Content panel */}
      <div
        ref={contentPanelRef}
        style={{
          flex: 1,
          padding: '16px 20px',
          minHeight: 200,
          maxHeight: `calc(100vh - ${topHeight + 64 + 34}px)`,
          overflowY: 'auto',
        }}
      >
        {!displaySection ? (
          <Typography variant="body2" style={{ color: '#888' }}>
            Select a section from the menu to view its content.
          </Typography>
        ) : (
          <SBSectionContentViewer
            key={displaySection.true_id}
            section={displaySection}
            inSinglePage={displaySection.single_page}
            onAllLoaded={handleAllLoaded}
          />
        )}
      </div>
    </div>
  );
}